import { useCallback } from "react";
import { useRouter } from "next/router";
import { toast } from "react-toastify";
import { Stripe } from "@stripe/stripe-js";
import {
  CheckoutCompleteData,
  useRecaptcha,
} from "@ecologi/react-components/src";
import {
  User,
  CAPTCHA_ACTIONS,
  ImpactShopBasketProduct,
  ImpactShopCheckoutProduct,
  Currency,
  Country,
  PAYMENT_TYPE,
} from "@ecologi/shared";
import {
  checkoutWithRegistration,
  clearAttributionCookie,
  formatAPIErrorForView,
  getMockedImpact,
  pushPurchase,
  toGA4Item,
} from "../../../lib";
import { clearAuthenticatedSignupPending } from "../../plans/authenticated-signup-return-to";
import type { Store } from "../../../store/store.types";

class HandledCheckoutError extends Error {}

interface BasketProductsResult {
  products: ImpactShopCheckoutProduct[];
  currency: Currency;
}

interface UseCheckoutWithRegistrationParams {
  stripe: Stripe | null;
  recaptcha: ReturnType<typeof useRecaptcha>;
  location: { countryCode: Country } | null;
  idempotencyKey: string;
  basket: ImpactShopBasketProduct[];
  basketProductsWithCurrency: () => BasketProductsResult;
  totalPriceOfBasket: () => number;
  sendAnalyticsEvent: (event: string, data?: Record<string, unknown>) => void;
  setUser: Store["setUser"];
  setUtmParams: (params: Record<string, string>) => void;
  resetIdempotencyKey: () => void;
  utmParams: Record<string, string | undefined>;
}

export function useCheckoutWithRegistration({
  stripe,
  recaptcha,
  location,
  idempotencyKey,
  basket,
  basketProductsWithCurrency,
  totalPriceOfBasket,
  sendAnalyticsEvent,
  setUser,
  setUtmParams,
  resetIdempotencyKey,
  utmParams,
}: UseCheckoutWithRegistrationParams) {
  const router = useRouter();

  const handleCheckoutWithRegistrationComplete = useCallback(
    async (data: CheckoutCompleteData) => {
      if (!stripe || !data.paymentMethodId) {
        const message = "Payment could not be processed. Please try again.";
        toast.error(message);
        throw new HandledCheckoutError(message);
      }

      try {
        const captchaToken = await recaptcha.execute(
          CAPTCHA_ACTIONS.IMPACT_SHOP
        );

        const { products, currency: basketCurrency } =
          basketProductsWithCurrency();

        const response = await checkoutWithRegistration({
          customer: {
            emailAddress: data.formData.email,
            firstName: data.formData.firstName,
            lastName: data.formData.lastName,
            location: location?.countryCode || "GB",
          },
          billingAddress: {
            companyName: data.formData.billingAddress.companyName || undefined,
            address1: data.formData.billingAddress.address1,
            address2: data.formData.billingAddress.address2 || undefined,
            city: data.formData.billingAddress.city,
            postcode: data.formData.billingAddress.postcode,
            country: data.formData.billingAddress.country,
          },
          basket: {
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.quantity,
              fundedBy: p.fundedBy || undefined,
              recipientEmail: p.recipientEmail || undefined,
            })),
            currency: basketCurrency,
            idempotencyKey,
            utmParams:
              Object.keys(utmParams).length > 0 ? utmParams : undefined,
          },
          paymentMethodId: data.paymentMethodId,
          captchaToken,
          accountType: data.formData.accountType || undefined,
        });

        let user = response.user;

        if (response.payment.status === "requires_action") {
          if (!response.payment.clientSecret) {
            const message =
              "Additional authentication is required. Please try again or use a different card.";
            toast.error(message);
            throw new HandledCheckoutError(message);
          }

          const confirmation = await stripe.confirmCardPayment(
            response.payment.clientSecret
          );

          if (
            confirmation.error ||
            confirmation.paymentIntent?.status !== "succeeded"
          ) {
            const message =
              confirmation.error?.message ||
              "3DS authentication failed for the card provided";
            toast.error(message);
            throw new HandledCheckoutError(message);
          }

          if (response.authentication?.accessToken) {
            user = await getMockedImpact(
              PAYMENT_TYPE.CARD,
              idempotencyKey,
              response.authentication.accessToken
            );
          }
        } else if (response.payment.status !== "succeeded") {
          const message =
            response.payment.message ||
            "Payment failed. Please try again or use a different card.";
          toast.error(message);
          throw new HandledCheckoutError(message);
        }

        setUser(new User(user), { isCheckingUserSession: false });
        clearAuthenticatedSignupPending();

        const totalPriceForReporting = totalPriceOfBasket() / 100;
        const ga4Items = basket.map((p) => toGA4Item(p, basketCurrency));

        pushPurchase({
          transaction_id: idempotencyKey,
          value: totalPriceForReporting,
          currency: basketCurrency,
          items: ga4Items,
        });

        sendAnalyticsEvent("impactShopPurchase", {
          revenue: totalPriceForReporting,
          currency: basketCurrency,
        });

        clearAttributionCookie();
        setUtmParams({});

        await router.push(`/impact-shop/checkout-success?type=card`);
      } catch (error) {
        resetIdempotencyKey();
        if (error instanceof HandledCheckoutError) {
          throw error;
        }
        toast.error(formatAPIErrorForView(error));
        throw error;
      }
    },
    [
      stripe,
      recaptcha,
      location,
      idempotencyKey,
      basket,
      basketProductsWithCurrency,
      totalPriceOfBasket,
      sendAnalyticsEvent,
      setUser,
      setUtmParams,
      router,
      resetIdempotencyKey,
      utmParams,
    ]
  );

  return { handleCheckoutWithRegistrationComplete };
}
